import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Truck, Star, ChevronRight, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/2542ea2343ad70d2fdfef51d2c8258693e141e8f7378a76197dd8af073c0e92c.png";
const WORKSHOP_BG = "https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/a9aa23d080e21c73e35c372654eb037c02db153c7a092318b7c9bfe07c16643a.png";

const BRANDS = [
  { name: 'Bosch', color: '#009FE3', bg: '#E3F4FC' },
  { name: 'DeWalt', color: '#FEC109', bg: '#FFF8E1' },
  { name: 'Makita', color: '#008DA8', bg: '#E0F4F7' },
  { name: 'Milwaukee', color: '#E3000B', bg: '#FDECEA' },
  { name: 'Hikoki', color: '#008000', bg: '#E8F5E9' },
  { name: 'Stanley', color: '#F7941D', bg: '#FFF3E0' },
  { name: 'Black+Decker', color: '#FF6B00', bg: '#FFF0E0' },
  { name: 'Ingco', color: '#0070B0', bg: '#E3F0F8' },
];

const CATEGORIES = [
  { name: 'Drills & Drivers',  img: 'https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/0c28b2807b7f71bf6be96be37f74dc3084152b8fa75f35b4e4b336ae91f51640.png', count: '5+' },
  { name: 'Angle Grinders',    img: 'https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/94c98b045f1a8da5ca437e1f5131265872f4e87836088b2d53f067d9f97c2f06.png', count: '5+' },
  { name: 'Rotary Hammers',    img: 'https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/0c5b9a2339556872dcbb049459996590c6900bf803d804db9341a3e192c02d36.png', count: '4+' },
  { name: 'Circular Saws',     img: 'https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/dd5d14e2d06a01996afbf19389fe51d7c73e7874ec67a068f59355c42afafc22.png', count: '3+' },
  { name: 'Impact Wrenches',   img: 'https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/5fa472fea077bf735e214658c1221da382cd69fb1fc4f58590901ed23487276f.png', count: '3+' },
  { name: 'Pressure Washers',  img: 'https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/ace491bc3a4be6853c2f02c3e1c2111e22399739099c1e26ea763d691f415d83.png', count: '3+' },
  { name: 'Sanders',           img: 'https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/45581c8d36b93ec85f0b6f86f9eea9aa8b4e1be15098a3db7513d661a0dbcc2a.png', count: '2+' },
  { name: 'Accessories',       img: 'https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/dd547f752f766d4136243bd22fa18b55f4b8ed0f38da5aa7cd78276034ab8d83.png', count: '3+' },
];

const TESTIMONIALS = [
  { name: 'Ahmed Al Rashidi', role: 'Contractor, Dubai', rating: 5, text: 'Best prices in UAE for genuine Bosch and DeWalt tools. My go-to shop for all my site requirements.' },
  { name: 'Sanjay Kumar', role: 'Workshop Owner, Sharjah', rating: 5, text: 'Ordered 3 Makita grinders - arrived in 2 days. All genuine with UAE warranty. Highly recommended.' },
  { name: 'Mohammed Al Zaabi', role: 'DIY Enthusiast, Abu Dhabi', rating: 5, text: 'Great selection and honest price comparison. You can see exactly what you save vs market price.' },
];

const WHY_US = [
  { icon: Shield, title: '100% Genuine', desc: 'All products are original and come with manufacturer warranty. No counterfeits.' },
  { icon: Truck, title: 'Fast UAE Delivery', desc: 'Same-day dispatch for orders before 2PM. Delivery across all UAE emirates.' },
  { icon: Zap, title: 'Best UAE Price', desc: 'We compare prices across UAE market and guarantee the most competitive deals.' },
  { icon: Star, title: 'Expert Support', desc: 'Our tool specialists can help you choose the right tool for your application.' },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [deals, setDeals] = useState([]);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const [featuredRes, ratedRes, dealsRes] = await Promise.all([
        axios.get(`${API}/products?featured=true&limit=8`),
        axios.get(`${API}/products?sort=rating&limit=4`),
        axios.get(`${API}/products?sort=price_low&limit=8`),
      ]);
      setFeatured(featuredRes.data.products || []);
      setTopRated(ratedRes.data.products || []);
      setDeals(dealsRes.data.products?.filter(p => p.compare_price) || []);
    } catch (e) {
      console.error('Failed to load products', e);
    }
  };

  return (
    <div className="bg-background min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-end pb-16 overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="Power Tools" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
          {/* Extra dark bottom for text readability */}
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10 pt-28">
          <div className="max-w-3xl">
            {/* Eyebrow */}
            <div className="flex items-center gap-2 mb-4 animate-fade-in-up">
              <div className="h-px w-8 bg-primary" />
              <span className="text-primary text-xs font-bold uppercase tracking-widest">UAE's #1 Power Tools Hub</span>
            </div>

            {/* Headline */}
            <h1 className="font-heading font-black uppercase leading-none text-white mb-4" style={{ fontSize: 'clamp(3rem, 8vw, 7rem)', animationDelay: '0.1s' }}>
              Power Meets<br />
              <span className="text-primary">Precision</span>
            </h1>

            <p className="text-white/70 text-base md:text-lg font-body mb-8 max-w-xl" style={{ animationDelay: '0.2s' }}>
              Your trusted hub for professional power tools in UAE. Bosch, DeWalt, Makita, Milwaukee and more — at the best market prices.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 mb-12">
              <button
                onClick={() => navigate('/products')}
                className="btn-primary flex items-center gap-2"
                data-testid="hero-shop-now-btn"
              >
                Shop Now <ArrowRight size={16} />
              </button>
              <button
                onClick={() => navigate('/products?sort=price_low')}
                className="btn-secondary flex items-center gap-2"
                data-testid="hero-best-deals-btn"
              >
                Best Deals
              </button>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8">
              {[['35+', 'Power Tools'], ['8+', 'Top Brands'], ['UAE', 'Fast Delivery'], ['100%', 'Genuine Products']].map(([n, l]) => (
                <div key={l}>
                  <div className="font-heading font-black text-2xl text-primary">{n}</div>
                  <div className="text-xs text-white/50 uppercase tracking-widest">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 right-8 z-10 hidden md:flex flex-col items-center gap-2">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-primary/50 to-transparent" />
          <span className="text-xs text-white/30 uppercase tracking-widest" style={{ writingMode: 'vertical-rl' }}>Scroll</span>
        </div>
      </section>

      {/* Trust bar */}
      <div className="bg-primary py-4">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-wrap justify-center gap-6 md:gap-12">
            {['Genuine Products', 'UAE Fast Delivery', 'Secure Checkout', 'Warranty Support'].map(trust => (
              <div key={trust} className="flex items-center gap-2">
                <span className="w-4 h-4 bg-black/20 flex items-center justify-center text-xs font-black">✓</span>
                <span className="text-black text-xs font-bold uppercase tracking-wide">{trust}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Categories */}
      <section className="py-16 md:py-24 container mx-auto px-4 md:px-6 max-w-7xl" data-testid="categories-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-px w-6 bg-primary" />
              <span className="text-primary text-xs font-bold uppercase tracking-widest">Browse</span>
            </div>
            <h2 className="section-heading text-3xl md:text-4xl text-white">Shop By Category</h2>
          </div>
          <Link to="/products" className="text-sm text-primary hover:text-white transition-colors flex items-center gap-1 font-semibold">
            All Products <ChevronRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {CATEGORIES.map((cat, i) => (
            <Link
              key={cat.name}
              to={`/products?category=${encodeURIComponent(cat.name)}`}
              className="group relative overflow-hidden bg-surface border border-white/5 hover:border-primary/40 transition-all duration-300"
              style={{ aspectRatio: '4/3' }}
              data-testid={`category-card-${i}`}
            >
              <img src={cat.img} alt={cat.name} className="w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-500" />
              <div className="absolute inset-0 flex flex-col items-start justify-end p-3">
                <p className="text-white font-heading font-bold uppercase text-base leading-tight">{cat.name}</p>
                <p className="text-primary text-xs font-mono">{cat.count} products</p>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            </Link>
          ))}
        </div>
      </section>

      {/* Popular Brands */}
      <section className="py-12 bg-surface border-y border-white/5" data-testid="brands-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-8">
            <span className="text-primary text-xs font-bold uppercase tracking-widest">Trusted By Professionals</span>
            <h2 className="section-heading text-2xl md:text-3xl text-white mt-1">Shop By Brand</h2>
          </div>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
            {BRANDS.map(brand => (
              <Link
                key={brand.name}
                to={`/products?brand=${encodeURIComponent(brand.name)}`}
                className="flex items-center justify-center py-4 px-3 bg-white/3 border border-white/5 hover:border-primary/40 hover:bg-white/8 transition-all duration-300 group"
                data-testid={`brand-card-${brand.name.toLowerCase()}`}
              >
                <span className="font-heading font-black text-xs md:text-sm uppercase tracking-tight text-white/60 group-hover:text-primary transition-colors whitespace-nowrap">
                  {brand.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Best Deals */}
      <section className="py-16 md:py-24 container mx-auto px-4 md:px-6 max-w-7xl" data-testid="deals-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-px w-6 bg-secondary" />
              <span className="text-secondary text-xs font-bold uppercase tracking-widest">Limited Time</span>
            </div>
            <h2 className="section-heading text-3xl md:text-4xl text-white">Best UAE Price Deals</h2>
            <p className="text-muted-foreground text-sm mt-1">Prices compared across UAE market. Save more, get genuine.</p>
          </div>
          <Link to="/products?sort=price_low" className="text-sm text-primary hover:text-white transition-colors flex items-center gap-1 font-semibold">
            View All <ChevronRight size={14} />
          </Link>
        </div>
        {deals.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {deals.slice(0, 8).map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="aspect-square shimmer-bg" />)}
          </div>
        )}
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-24 bg-surface border-y border-white/5" data-testid="featured-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-px w-6 bg-primary" />
                <span className="text-primary text-xs font-bold uppercase tracking-widest">Handpicked</span>
              </div>
              <h2 className="section-heading text-3xl md:text-4xl text-white">Featured Products</h2>
            </div>
            <Link to="/products?featured=true" className="text-sm text-primary hover:text-white transition-colors flex items-center gap-1 font-semibold">
              View All <ChevronRight size={14} />
            </Link>
          </div>
          {featured.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {featured.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="aspect-square shimmer-bg" />)}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 container mx-auto px-4 md:px-6 max-w-7xl" data-testid="why-choose-section">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px w-6 bg-primary" />
              <span className="text-primary text-xs font-bold uppercase tracking-widest">Why Us</span>
            </div>
            <h2 className="section-heading text-3xl md:text-4xl text-white mb-4">Why Choose M N M Tools Hub?</h2>
            <p className="text-muted-foreground mb-8">
              We are UAE's dedicated power tools marketplace. Every tool is sourced directly from authorized distributors, ensuring authenticity and full warranty coverage.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {WHY_US.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="p-4 border border-white/5 bg-surface hover:border-primary/30 transition-all duration-300">
                  <div className="w-8 h-8 bg-primary/10 border border-primary/20 flex items-center justify-center mb-3">
                    <Icon size={15} className="text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-base text-white mb-1">{title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={WORKSHOP_BG} alt="Workshop" className="w-full h-96 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-background/40 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 p-4 bg-black/80 border border-primary/20">
              <p className="text-primary text-xs font-bold uppercase tracking-widest mb-1">UAE Price Guarantee</p>
              <p className="text-white text-sm">
                We compare prices across all major UAE suppliers and guarantee you're getting the best deal available.
              </p>
              <p className="text-white/40 text-xs mt-1">*Prices updated weekly. Subject to availability.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Top Rated */}
      {topRated.length > 0 && (
        <section className="py-16 bg-surface border-y border-white/5">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-px w-6 bg-primary" />
              <span className="text-primary text-xs font-bold uppercase tracking-widest">Top Rated</span>
            </div>
            <h2 className="section-heading text-3xl md:text-4xl text-white mb-8">Highest Rated Tools</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {topRated.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="py-16 md:py-24 container mx-auto px-4 md:px-6 max-w-7xl" data-testid="testimonials-section">
        <div className="text-center mb-10">
          <span className="text-primary text-xs font-bold uppercase tracking-widest">Reviews</span>
          <h2 className="section-heading text-3xl md:text-4xl text-white mt-1">What Customers Say</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="p-6 border border-white/5 bg-surface hover:border-primary/20 transition-all duration-300">
              <div className="flex mb-3">
                {[1,2,3,4,5].map(s => <Star key={s} size={12} className="text-primary fill-primary" />)}
              </div>
              <p className="text-sm text-white/80 leading-relaxed mb-4">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/20 border border-primary/30 flex items-center justify-center font-bold text-primary text-sm">
                  {t.name[0]}
                </div>
                <div>
                  <p className="text-sm font-bold text-white">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 bg-primary" data-testid="newsletter-section">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-xl mx-auto text-center">
            <h2 className="section-heading text-3xl md:text-4xl text-black mb-2">Get Exclusive Deals</h2>
            <p className="text-black/70 text-sm mb-6">Subscribe for UAE price alerts, new arrivals, and exclusive discounts.</p>
            {subscribed ? (
              <p className="text-black font-bold text-base">Thank you for subscribing!</p>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); if(email) setSubscribed(true); }} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="flex-1 bg-black/10 border border-black/20 text-black placeholder-black/40 px-4 py-3 outline-none focus:border-black/40 text-sm"
                  style={{ borderRadius: 0 }}
                  data-testid="newsletter-email-input"
                  required
                />
                <button type="submit" className="bg-black text-white font-bold uppercase tracking-wider px-6 py-3 hover:bg-surface transition-colors text-sm" data-testid="newsletter-submit-btn">
                  Subscribe
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
